import { useState } from "react";
import { X, Loader2, ArrowLeftRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Super-admin modal to reassign an application's placement (association)
 * or mentor with an audited reason. On confirm, calls
 * POST /api/applications/{id}/reassign.
 *
 * Props:
 *  - app: application object (must include id, student_name, assigned_*_id).
 *  - mode: "association" | "mentor"
 *  - options: array of { id, name } to populate the dropdown.
 *  - onClose(): close without saving.
 *  - onSaved(reason): called after successful save.
 */
export default function ReassignModal({ app, mode, options, onClose, onSaved }) {
  const isAssoc = mode === "association";
  const currentId = isAssoc ? app.assigned_association_id : app.assigned_mentor_id;
  const [nextId, setNextId] = useState(currentId || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const label = isAssoc ? "الجمعية" : "الموجّه";
  const testKey = isAssoc ? "reassign-association" : "reassign-mentor";

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("سبب التغيير مطلوب (5 أحرف على الأقل)");
      return;
    }
    if ((nextId || "") === (currentId || "")) {
      toast.error(`اختر ${label} مختلفة`);
      return;
    }
    setBusy(true);
    try {
      const body = { reason: reason.trim() };
      if (isAssoc) body.new_association_id = nextId;
      else body.new_mentor_id = nextId;
      await api.post(`/applications/${app.id}/reassign`, body);
      toast.success("تم تحديث الإسناد وإرسال الإشعارات");
      onSaved?.(reason.trim());
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:px-4" data-testid={`${testKey}-modal`}>
      <form
        onSubmit={submit}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col animate-sheet-up sm:animate-fade-in-fast"
        style={{ maxHeight: "92dvh" }}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إسناد جديد</div>
            <h2 className="mt-1 text-lg font-heading font-black text-slate-900 flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-teal-700" />
              تغيير {label} لـ {app.student_name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="إغلاق"
            data-testid={`${testKey}-close`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <label className="block">
            <span className="text-xs font-bold text-slate-600">{label} الجديدة</span>
            <select
              value={nextId}
              onChange={(e) => setNextId(e.target.value)}
              className="mt-1.5 w-full min-h-11 rounded-xl border border-slate-300 px-3 text-sm bg-white"
              data-testid={`${testKey}-select`}
              required
            >
              <option value="">اختر {label}…</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-slate-600">
              سبب التغيير <span className="text-rose-600">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              minLength={5}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="مثال: عدم توفر مقاعد في الجمعية الحالية، أو تعارض جدول الموجّه…"
              data-testid={`${testKey}-reason`}
              required
            />
            <span className="mt-1 block text-[11px] text-slate-500">
              السبب يُسجَّل في سجل التدقيق ويُرسَل ضمن إشعار الطرفين.
            </span>
          </label>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 leading-6">
            سيتم إرسال إشعار بريد داخلي للطرف السابق والطرف الجديد، وتصحيح عدّاد المقاعد إذا كانت الرحلة معتمدة.
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-xl px-4 text-sm font-bold text-slate-600 hover:bg-slate-100"
            data-testid={`${testKey}-cancel`}
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 min-h-11 rounded-xl bg-teal-700 px-5 text-sm font-bold text-white disabled:opacity-50"
            data-testid={`${testKey}-submit`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "جارٍ الحفظ…" : "تأكيد التغيير"}
          </button>
        </div>
      </form>
    </div>
  );
}
